import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  KeyboardEvent,
} from "react"
import cx from "classnames"
import { css } from "@emotion/react"
import { PaymentMethodIcon } from "source/payments/components/icons/PaymentMethodIcon"
import { PaymentMethod } from "../types"
import { Icon } from "source/shared/components"
import "../styles/styles.css"
import useOutsideHandler from "../hooks/useOutsideHandler"

interface PaymentMethodSelectorProps {
  onAddClick: () => void
  onChange: (id: string | null) => void
  paymentMethods: PaymentMethod[]
  selectedId: string | null
  currentPaymentMethodId?: string | null
}

const PaymentMethodLabel: React.FC<PaymentMethod> = (paymentMethod) => {
  const suffix = !paymentMethod.verified ? "Unverified" : null

  return (
    <>
      <PaymentMethodIcon
        brand={paymentMethod.brand}
        isCard={paymentMethod.method_type === "card"}
      />
      <span className="f-1 ta-l">
        {paymentMethod.brand} {paymentMethod.last4}
        {suffix && ` (${suffix})`}
      </span>
    </>
  )
}

export default function PaymentMethodSelector({
  onAddClick,
  onChange,
  paymentMethods,
  selectedId,
  currentPaymentMethodId,
}: PaymentMethodSelectorProps) {
  const [activeId, setActiveId] = useState<string | null>(
    selectedId?.toString() || null,
  )
  const [isExpanded, setIsExpanded] = useState<boolean>(!selectedId)
  const selectedPaymentMethod = paymentMethods.find(
    ({ id }) => id === selectedId,
  )

  const containerRef = useRef<HTMLDivElement>(null)
  const radioGroupRef = useRef<HTMLDivElement>(null)
  const toggleButtonRef = useRef<HTMLButtonElement>(null)
  const toggleIntentRef = useRef<boolean>(false)

  useEffect(() => {
    if (selectedId !== undefined) {
      setActiveId(selectedId)
      setIsExpanded(false)
    }
  }, [selectedId, setIsExpanded])

  useLayoutEffect(() => {
    if (toggleIntentRef.current) {
      if (isExpanded) {
        const checkedRadio =
          radioGroupRef.current?.querySelector<HTMLInputElement>(
            "input:checked",
          )
        checkedRadio
          ? checkedRadio.focus()
          : radioGroupRef.current
              ?.querySelector<HTMLInputElement>("input")
              ?.focus()
      } else {
        toggleButtonRef.current?.focus()
      }
      toggleIntentRef.current = false
    }
  }, [isExpanded])

  useOutsideHandler(containerRef, () => {
    if (isExpanded) {
      commitSelection()
    }
  })

  function handleKeydown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      toggleIntentRef.current = true
      commitSelection()
    }
  }

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    setActiveId(event.target.value)
  }

  function handleToggleButtonClick() {
    toggleIntentRef.current = true
    if (isExpanded) {
      commitSelection()
    } else {
      setIsExpanded(true)
    }
  }

  function handleOptionClick(id: string) {
    toggleIntentRef.current = true
    commitSelection(id)
  }

  function handleAddClick() {
    onAddClick()
  }

  function commitSelection(id = activeId) {
    if (id !== selectedId) {
      setActiveId(id)
      onChange(id)
    }
    setIsExpanded(false)
  }

  return (
    <div className="mb-2" ref={containerRef}>
      <label htmlFor="payment-method-selector-label">Payment method</label>

      <div className="payment-method-selector">
        <button
          type="button"
          className="btn text-btn payment-method-selector__toggle"
          onClick={handleToggleButtonClick}
          ref={toggleButtonRef}
          aria-controls="payment-method-selector-menu"
          aria-expanded={isExpanded}
        >
          {selectedPaymentMethod && !isExpanded ? (
            <PaymentMethodLabel {...selectedPaymentMethod} />
          ) : (
            <span className="f-1 ta-l">Choose payment method</span>
          )}
          <Icon symbol="general#down-chevron" aria-hidden />
        </button>

        <div
          className={cx("fd-c", { "d-n": !isExpanded, "d-f": isExpanded })}
          id="payment-method-selector-menu"
          data-testid="payment-method-selector-menu"
        >
          <div
            role="radiogroup"
            ref={radioGroupRef}
            aria-labelledby="payment-method-selector-label"
          >
            {paymentMethods.map((paymentMethod) => {
              const isUnverified = !paymentMethod.verified
              const isCurrent = paymentMethod.id === currentPaymentMethodId
              const isDisabled = isUnverified && !isCurrent

              return (
                <div
                  className={cx("payment-method-selector__option", {
                    "payment-method-selector__option--active":
                      activeId === paymentMethod.id,
                  })}
                  css={css`
                    ${activeId === paymentMethod.id &&
                    "background-color: rgba(19, 124, 114, 0.05);"}
                  `}
                  key={paymentMethod.id}
                >
                  <input
                    type="radio"
                    className="radio"
                    id={`payment_method_${paymentMethod.id}`}
                    name="paymentMethodSelector"
                    onChange={handleChange}
                    onKeyDown={handleKeydown}
                    value={paymentMethod.id}
                    checked={activeId === paymentMethod.id}
                    disabled={isDisabled}
                    css={css`
                      .radio:checked + .radio-label:before {
                        background-color: var(--color-brand);
                      }
                    `}
                  />
                  <label // eslint-disable-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
                    className="radio-label"
                    htmlFor={`payment_method_${paymentMethod.id}`}
                    onClick={() => handleOptionClick(paymentMethod.id)}
                  >
                    <PaymentMethodLabel {...paymentMethod} />
                  </label>
                </div>
              )
            })}
          </div>
          <div className="payment-method-selector__add-button-container">
            <button
              type="button"
              className="payment-method-selector__add-button btn compact-btn"
              onClick={handleAddClick}
            >
              <span
                className="d-f ai-c jc-c circle"
                css={css`
                  backgroundColor: "var(--color-tint6)",
                  width: "1.5em",
                  height: "1.5em",
                `}
              >
                <Icon symbol="general#plus" aria-hidden />
              </span>
              Add a payment method
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
